import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Lock } from 'lucide-react';
import { Button } from './ui/button';
import LanguageSwitch from './LanguageSwitch';

const Header = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Detectamos el idioma activo actual
  const idiomaActivo = i18n.language || 'es';
  const esIngles = idiomaActivo.toLowerCase().includes('en');

  const navItems = [
    { label: t('nav.home'), path: '/' },
    { label: t('nav.about'), path: '/about' },
    { label: t('nav.services'), path: '/services' },
    { label: t('nav.quality'), path: '/quality-policy' },
    { 
      // Si el idioma es inglés muestra 'Portfolio', si no, evalúa tu JSON o usa 'Portafolio' por defecto
      label: esIngles ? 'Portfolio' : t('nav.portfolio', 'Portafolio'), 
      path: '/portfolio' 
    },
    { label: t('nav.contact'), path: '/contact' }
  ];

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-sm shadow-md'
          : 'bg-white'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/"
            className="flex items-center cursor-pointer hover:opacity-80 transition-opacity"
          >
            <span className="text-xl font-bold text-primary">Voltaris</span>
            <span className="text-xl font-light text-secondary ml-1">Industry & Ren.</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`font-medium transition-colors duration-200 relative ${
                  isActive(item.path)
                    ? 'text-primary'
                    : 'text-foreground hover:text-primary'
                }`}
              >
                {item.label}
                {isActive(item.path) && (
                  <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary"></span>
                )}
              </Link>
            ))}
            
            <div className="flex items-center gap-3 pl-2 border-l border-slate-200">
              <LanguageSwitch />
              
              {/* Acceso discreto al panel de administración */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/admin/login')}
                className="text-slate-400 hover:text-primary h-9 w-9"
                title="CMS Login"
              >
                <Lock size={16} />
              </Button>
            </div>
          </nav>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            <LanguageSwitch />
            
            {/* Acceso discreto al panel de administración en móvil */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin/login')}
              className="text-slate-400 hover:text-primary"
            >
              <Lock size={18} />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <nav className="md:hidden pb-6 animate-in slide-in-from-top">
            <div className="flex flex-col space-y-4">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`text-left font-medium py-2 transition-colors duration-200 ${
                    isActive(item.path)
                      ? 'text-primary font-bold'
                      : 'text-foreground hover:text-primary'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;